import { useState } from 'react'
import { privatize } from '@/lib/utils/revenueUtils'
import { SegmentedControl } from '@/components/shared/layout'
import { Card } from '@/components/ui/card'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { FunnelSteps, type FunnelItem } from './FunnelStrip'
import type { AdsAttributionCampaign } from './hooks/useAdsAttribution'
import type { AttributionFunnelData } from './hooks/useAttributionFunnel'
import type { GoalType } from './AttributionCampaigns'
import { fmtCount, fmtMoney, stepRate } from './attributionView'

/**
 * Visit → trial → paying customer for everyone, then narrowed: by the page's
 * filters (country, source, page...) and by the selector here (all ads, one
 * platform, one campaign). Ad steps (shown, clicked) are the platforms' own
 * totals for matched campaigns, so they only show for an ad selection with no
 * page filters: the platforms report no country or page split here.
 * "Matched to a visit" is a filter of its own (All · Matched · Not matched):
 * All counts every trial and payment, including Stripe records no visit was
 * linked to. A record with no visit has no country, source or campaign, so
 * page filters and ad selections only ever count matched ones.
 */

export type FunnelSelection =
  | { kind: 'everyone' }
  | { kind: 'ads'; platform: 'google' | 'meta' | null }
  | { kind: 'campaign'; utmCampaign: string }

export const selectionValue = (s: FunnelSelection) =>
  s.kind === 'everyone' ? 'everyone' : s.kind === 'ads' ? `ads:${s.platform ?? 'all'}` : `campaign:${s.utmCampaign}`

export function parseSelection(value: string): FunnelSelection {
  if (value.startsWith('campaign:')) return { kind: 'campaign', utmCampaign: value.slice('campaign:'.length) }
  if (value === 'ads:google' || value === 'ads:meta') return { kind: 'ads', platform: value.slice(4) as 'google' | 'meta' }
  if (value === 'ads:all') return { kind: 'ads', platform: null }
  return { kind: 'everyone' }
}

const onPlatform = (c: AdsAttributionCampaign, platform: 'google' | 'meta') =>
  c.platforms.some((p) => p.toLowerCase().includes(platform === 'google' ? 'google' : 'meta'))

/** Matched campaigns the selection covers; null = everyone. */
export function selectedCampaigns(campaigns: AdsAttributionCampaign[], selection: FunnelSelection): AdsAttributionCampaign[] | null {
  if (selection.kind === 'everyone') return null
  if (selection.kind === 'campaign') return campaigns.filter((c) => c.utm_campaign === selection.utmCampaign)
  return selection.platform ? campaigns.filter((c) => onPlatform(c, selection.platform!)) : campaigns
}

export type MatchFilter = 'all' | 'matched' | 'unmatched'

const MATCH_OPTIONS: { value: MatchFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'matched', label: 'Matched to a visit' },
  { value: 'unmatched', label: 'Not matched' },
]

type Bucket = { trials: number; payments: number; customers: number; revenue: number }
const n = (v: number | null | undefined) => v ?? 0

/**
 * Trials, payments, customers and revenue for one match filter. Matched
 * includes records matched to a visit on another of the account's sites, but
 * only when no page filter is set: under a filter that bucket means "outside
 * the filter" instead.
 */
export function funnelBucket(f: AttributionFunnelData['funnel'], match: MatchFilter, unfiltered: boolean): Bucket {
  const here = { trials: n(f.trials), payments: n(f.payments), customers: n(f.customers), revenue: n(f.revenue) }
  const other = unfiltered
    ? { trials: n(f.elsewhere.trials), payments: n(f.elsewhere.payments), customers: n(f.elsewhere.customers), revenue: n(f.elsewhere.revenue) }
    : { trials: 0, payments: 0, customers: 0, revenue: 0 }
  const none = { trials: n(f.not_linked.trials), payments: n(f.not_linked.payments), customers: n(f.not_linked.customers), revenue: n(f.not_linked.revenue) }
  const add = (...bs: Bucket[]) => bs.reduce((a, b) => ({
    trials: a.trials + b.trials, payments: a.payments + b.payments, customers: a.customers + b.customers, revenue: a.revenue + b.revenue,
  }))
  if (match === 'unmatched') return none
  if (match === 'matched' || !unfiltered) return add(here, other)
  return add(here, other, none)
}

export default function AttributionFunnel({
  data,
  isLoading,
  error,
  campaigns,
  selection,
  onSelectionChange,
  filtersActive,
  provider,
  needsRevenue = null,
  privacyMode = false,
  goal = 'revenue' as GoalType,
}: {
  data: AttributionFunnelData | null
  isLoading: boolean
  error: string | null
  /** Matched ad campaigns (the Campaigns table rows) */
  campaigns: AdsAttributionCampaign[]
  selection: FunnelSelection
  onSelectionChange: (s: FunnelSelection) => void
  /** Page filters (country, source...) are set */
  filtersActive: boolean
  provider: string
  /** Revenue connector name when none is connected */
  needsRevenue?: string | null
  goal?: GoalType
  privacyMode?: boolean
}) {
  const money = (v: number) => privatize(fmtMoney(v), privacyMode)
  const chosen = selectedCampaigns(campaigns, selection)
  const isAds = chosen != null
  const hasGoogle = campaigns.some((c) => onPlatform(c, 'google'))
  const hasMeta = campaigns.some((c) => onPlatform(c, 'meta'))

  const spend = (chosen ?? []).reduce((s, c) => s + (c.spend || 0), 0)
  const impressions = (chosen ?? []).reduce((s, c) => s + (c.impressions || 0), 0)
  const clicks = (chosen ?? []).reduce((s, c) => s + (c.clicks || 0), 0)
  // Platform numbers have no country/page split, so they drop out under page filters.
  const showAdSteps = isAds && !filtersActive && (impressions > 0 || clicks > 0)

  const f = data?.funnel
  const visitors = f?.visitors ?? 0
  // Only the everyone funnel with no page filters can count what matched no visit.
  const canSplit = !isAds && !filtersActive && !data?.filtered
  const [matchPick, setMatchPick] = useState<MatchFilter>('all')
  const match: MatchFilter = canSplit ? matchPick : 'matched'
  const bucket = f ? funnelBucket(f, match, canSplit) : null
  const matchedBucket = f ? funnelBucket(f, 'matched', canSplit) : null
  const unmatchedBucket = f ? funnelBucket(f, 'unmatched', canSplit) : null
  const revenueUnavailable = f != null && f.trials == null
  const elsewhereTrials = canSplit ? n(f?.elsewhere.trials) : 0
  const elsewhereCustomers = canSplit ? n(f?.elsewhere.customers) : 0

  // "1 matched · 3 not matched": what All is made of, so the split is visible
  const split = (matched: number, unmatched: number) =>
    match === 'all' && unmatched > 0 ? `${fmtCount(matched)} matched · ${fmtCount(unmatched)} not matched to a visit` : null
  const onOtherSites = (count: number, what: string) =>
    match === 'matched' && count > 0 ? `${fmtCount(count)} ${what} on your other sites` : null

  const items: FunnelItem[] = []
  if (showAdSteps) {
    items.push(
      { label: 'Ads shown', value: fmtCount(impressions) },
      {
        label: 'Clicked',
        value: fmtCount(clicks),
        note: clicks > 0 && spend > 0 ? `${money(spend / clicks)} a click` : null,
        rateIn: stepRate(clicks, impressions),
      },
    )
  }
  const neverLoaded = showAdSteps && clicks > visitors ? clicks - visitors : 0
  items.push(match === 'unmatched' ? {
    label: 'Visited your site',
    value: '—',
    note: 'No visit was linked to these',
    noteTone: 'muted',
  } : {
    label: isAds ? 'Landed on your site' : 'Visited your site',
    value: fmtCount(visitors),
    note: neverLoaded > 0
      ? `${fmtCount(neverLoaded)} ${neverLoaded === 1 ? 'click' : 'clicks'} never loaded`
      : isAds ? null : filtersActive ? 'Matching the filters' : 'All sources',
    noteTone: 'muted',
    warn: showAdSteps && clicks > 0 && visitors / clicks < 0.6,
    rateIn: showAdSteps ? stepRate(visitors, clicks) : null,
  })

  // For the Forms goal, insert form_submissions as a step between visits and trials
  const formSubmissions = f?.form_submissions ?? null
  if (goal === 'forms' && formSubmissions != null) {
    items.push({
      label: 'Submitted a form',
      value: privatize(fmtCount(formSubmissions), privacyMode),
      rateIn: match === 'unmatched' ? null : stepRate(formSubmissions, visitors),
    })
  }

  if (needsRevenue || revenueUnavailable || !bucket) {
    items.push({
      placeholder: true,
      label: 'Started a trial · Paying customers',
      title: `Needs ${needsRevenue ?? provider}`,
      body: `Trials and payments come from ${needsRevenue ?? provider}, matched back to these ${fmtCount(visitors)} visits`,
    })
  } else {
    const { trials, payments, customers, revenue } = bucket
    items.push(
      {
        label: 'Started a trial',
        value: privatize(fmtCount(trials), privacyMode),
        note: isAds && trials > 0 && spend > 0
          ? `${money(spend / trials)} each`
          : split(matchedBucket!.trials, unmatchedBucket!.trials) ?? onOtherSites(elsewhereTrials, elsewhereTrials === 1 ? 'trial' : 'trials'),
        rateIn: match === 'unmatched' ? null : stepRate(trials, visitors),
      },
      {
        label: `Paying customers · ${provider}`,
        value: privatize(fmtCount(customers), privacyMode),
        note: [
          revenue ? `${money(revenue)} collected` : null,
          payments > customers ? `${fmtCount(payments)} payments` : null,
          isAds && customers && spend > 0 ? `${money(spend / customers)} each` : null,
        ].filter(Boolean).join(' · ') || null,
        noteTone: 'ink',
        extra: (() => {
          const line = split(matchedBucket!.customers, unmatchedBucket!.customers)
            ?? onOtherSites(elsewhereCustomers, elsewhereCustomers === 1 ? 'customer' : 'customers')
          return line ? <span className="text-xs leading-4 text-ui-meta">{line}</span> : null
        })(),
        rateIn: stepRate(customers, trials),
      },
    )
  }

  const subtitle = [
    selection.kind === 'everyone' ? 'Every source'
      : selection.kind === 'campaign' ? (chosen?.[0]?.campaign ?? selection.utmCampaign)
      : selection.platform === 'google' ? 'Google ads' : selection.platform === 'meta' ? 'Meta ads' : 'All ad campaigns',
    filtersActive ? 'filters applied' : null,
    // Paid in the range, renewals included: not the count of active subscribers
    'customers who paid in this range',
    isAds && filtersActive ? 'ad impressions and clicks hidden, platforms report no split by these filters' : null,
  ].filter(Boolean).join(' · ')

  const picker = campaigns.length > 0 ? (
    <Select value={selectionValue(selection)} onValueChange={(v) => onSelectionChange(parseSelection(v))}>
      <SelectTrigger size="sm" className="h-8 w-auto min-w-[160px] text-[13px]" aria-label="Narrow the funnel">
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end">
        <SelectItem value="everyone">Everyone</SelectItem>
        <SelectItem value="ads:all">All ad campaigns</SelectItem>
        {hasGoogle && hasMeta && <SelectItem value="ads:google">Google ads</SelectItem>}
        {hasGoogle && hasMeta && <SelectItem value="ads:meta">Meta ads</SelectItem>}
        <SelectGroup>
          <SelectLabel>Campaigns</SelectLabel>
          {campaigns.map((c) => (
            <SelectItem key={c.utm_campaign} value={`campaign:${c.utm_campaign}`}>{c.campaign}</SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  ) : null

  return (
    <Card className="gap-0 py-0">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 pt-4 pb-1">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
          <h3 className="text-sm font-medium leading-[18px] text-foreground">{goal === 'forms' ? 'From visit to form' : goal === 'trials' ? 'From visit to trial' : 'From visit to payment'}</h3>
          <span className="text-[13px] leading-4 text-ui-secondary">{subtitle}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canSplit ? (
            <SegmentedControl options={MATCH_OPTIONS} value={match} onChange={setMatchPick} />
          ) : (
            <span className="text-xs leading-4 text-muted-foreground">Matched to a visit only</span>
          )}
          {picker}
        </div>
      </div>
      {error && !data ? (
        <p className="px-5 pt-3 pb-[18px] text-[13px] text-muted-foreground">The funnel couldn't be loaded. Refresh to try again.</p>
      ) : isLoading && !data ? (
        <div className="flex gap-6 px-5 pt-3.5 pb-[18px]" aria-busy>
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex flex-1 flex-col gap-1.5">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-7 w-16" />
              <Skeleton className="h-4 w-24" />
            </div>
          ))}
        </div>
      ) : (
        <FunnelSteps items={items} />
      )}
    </Card>
  )
}
